import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, ChevronRight, CheckCircle2, Clock3, FileText, Briefcase, MapPin } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { fmtDate, fmtDateLong, weekStart } from "@/lib/format";
import { StatusBadge, LoadingBlock } from "@/components/Shared";

export default function EmployeeDashboard() {
  const { user, meta } = useAuth();
  const [recent, setRecent] = useState(null);
  const [week, setWeek] = useState(0);
  const [projects, setProjects] = useState([]);
  const today = meta.today;

  useEffect(() => {
    if (!today) return;
    (async () => {
      const [r, w, o] = await Promise.all([
        api.get("/reports", { params: { limit: 6, mine: true } }),
        api.get("/reports", { params: { start: weekStart(today), end: today, limit: 200, mine: true } }),
        api.get("/form-options"),
      ]);
      setRecent(r.data.items);
      setWeek(w.data.items.filter((x) => x.status !== "draft").length);
      setProjects(o.data.projects);
    })();
  }, [today]);

  if (!recent) return <LoadingBlock />;
  const todays = recent.filter((r) => r.report_date === today);
  const draft = todays.find((r) => r.status === "draft");
  const submitted = todays.filter((r) => r.status !== "draft");
  const last = recent.find((r) => r.status !== "draft");

  return (
    <div className="space-y-6 fade-up">
      <div>
        <p className="eyebrow">{fmtDateLong(today)}</p>
        <h1 className="mt-1 text-4xl font-bold text-slate-900" data-testid="employee-welcome">Welcome, {user.first_name}</h1>
      </div>

      <Link to={draft ? `/reports/${draft.id}/edit` : "/reports/new"} data-testid="submit-daily-report-button"
        className="group flex min-h-[96px] items-center justify-between rounded-lg bg-[#5C241E] px-6 py-5 text-white shadow-[0_10px_30px_-12px_rgba(92,36,30,0.7)] transition-transform active:scale-[0.99]">
        <div>
          <p className="font-display text-3xl font-bold leading-none">{draft ? "Continue Today's Draft" : "Submit Daily Report"}</p>
          <p className="mt-2 text-sm text-white/75">{draft ? `${draft.project_name} · saved draft` : "Footage, comments & photos in a few minutes"}</p>
        </div>
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-white/15 transition-transform group-hover:scale-105">
          {draft ? <ChevronRight className="h-7 w-7" /> : <Plus className="h-7 w-7" />}
        </span>
      </Link>

      <div className="panel flex items-center gap-4 p-4" data-testid="today-status-card">
        {submitted.length ? <CheckCircle2 className="h-9 w-9 shrink-0 text-emerald-600" /> : <Clock3 className="h-9 w-9 shrink-0 text-amber-600" />}
        <div className="min-w-0">
          <p className="font-semibold text-slate-900" data-testid="today-status-text">
            {submitted.length ? `Today's report submitted (${submitted.length})` : "No report submitted today yet"}
          </p>
          <p className="truncate text-sm text-slate-500">{submitted.length ? submitted.map((s) => s.project_name).join(", ") : draft ? "You have a draft in progress." : "Tap the button above to start."}</p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="panel p-4" data-testid="stat-week-reports">
          <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500">Reports this week</p>
          <p className="font-mono-num mt-1 text-3xl font-semibold">{week}</p>
        </div>
        <div className="panel p-4" data-testid="stat-last-submitted">
          <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500">Last submitted</p>
          <p className="mt-2 text-base font-semibold">{last ? fmtDate(last.report_date) : "—"}</p>
          <p className="truncate text-xs text-slate-500">{last?.project_name}</p>
        </div>
      </div>

      <section>
        <div className="mb-2 flex items-center justify-between">
          <h2 className="font-display text-xl font-semibold">Recent reports</h2>
          <Link to="/history" className="text-sm font-semibold text-[#5C241E]" data-testid="view-all-history-link">View all</Link>
        </div>
        {recent.length ? (
          <div className="panel divide-y divide-border">
            {recent.map((r) => (
              <Link key={r.id} to={r.status === "draft" ? `/reports/${r.id}/edit` : `/reports/${r.id}`} data-testid={`recent-report-${r.id}`}
                className="flex items-center gap-3 px-4 py-3 transition-colors hover:bg-slate-50">
                <FileText className="h-5 w-5 shrink-0 text-[#5C241E]" />
                <div className="min-w-0 flex-1">
                  <p className="truncate font-semibold text-slate-900">{r.project_name}</p>
                  <p className="text-xs text-slate-500">{fmtDate(r.report_date)} · {r.photo_count} photos</p>
                </div>
                <StatusBadge status={r.status} />
              </Link>
            ))}
          </div>
        ) : <p className="panel p-4 text-sm text-slate-500">No reports yet.</p>}
      </section>

      {projects.length > 0 && (
        <section>
          <h2 className="mb-2 font-display text-xl font-semibold">Active projects</h2>
          <div className="grid gap-2 sm:grid-cols-2">
            {projects.map((p) => (
              <div key={p.id} className="panel p-3" data-testid={`active-project-${p.id}`}>
                <p className="flex items-center gap-2 font-semibold"><Briefcase className="h-4 w-4 text-[#5C241E]" />{p.name}</p>
                <p className="mt-0.5 text-xs text-slate-500">Job # {p.job_number || "—"}{p.location && <> · <MapPin className="inline h-3 w-3" /> {p.location}</>}</p>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
